import { useCallback, useRef, useState } from 'react';

/**
 * A hook that provides a mechanism to run asynchronous operations and track their completion.
 * It allows you to run multiple operations concurrently and provides a way to wait for all of them to complete.
 *
 * @returns An object containing methods to run operations, wait for their completion, and check if any operations are pending.
 */
export const useAsyncBarrier = () => {
  const [activeOperationCount, setActiveOperationCount] = useState(0);
  const activeOperationsRef = useRef<Set<Promise<unknown>>>(new Set());

  const run = useCallback(<Result>(operation: () => Promise<Result>) => {
    const trackedOperation = Promise.resolve()
      .then(operation)
      .finally(() => {
        activeOperationsRef.current.delete(trackedOperation);
        setActiveOperationCount(activeOperationsRef.current.size);
      });

    activeOperationsRef.current.add(trackedOperation);
    setActiveOperationCount(activeOperationsRef.current.size);
    return trackedOperation;
  }, []);

  const wait = useCallback(async () => {
    while (activeOperationsRef.current.size > 0) {
      await Promise.all(activeOperationsRef.current);
    }
  }, []);

  return {
    run,
    wait,
    isPending: activeOperationCount > 0,
  };
};
